import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import ProductItem from './ProductItem';

const RecentlyViewed = () => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isAnimating, setIsAnimating] = useState(false);

  const carouselRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // ✅ Instant scroll restore (Optional, best practice!)
  useLayoutEffect(() => {
    const savedPosition = sessionStorage.getItem('recentlyViewedScroll');
    if (savedPosition) {
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedPosition, 10));
        document.body.style.pointerEvents = 'none';
        setTimeout(() => {
          document.body.style.pointerEvents = '';
          sessionStorage.removeItem('recentlyViewedScroll'); // ✅ Clean up
        }, 50);
      });
    }
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem('recentlyViewedProducts');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setRecentlyViewed(parsed);
        }
      } catch (error) {
        console.error('Error parsing recently viewed products:', error);
        sessionStorage.removeItem('recentlyViewedProducts');
      }
    }
  }, []);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width < 640) setVisibleCount(2);
      else if (width < 768) setVisibleCount(3);
      else setVisibleCount(4);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (diff > threshold) nextSlide();
    else if (diff < -threshold) prevSlide();

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const nextSlide = () => {
    if (currentIndex < recentlyViewed.length - visibleCount && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => prev + 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => prev - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  if (!recentlyViewed || recentlyViewed.length === 0) return null;

  return (
    <div className="py-12 relative overflow-hidden">
      {/* Decorative circles */}

      {/* Heading */}
      <div className="relative z-10 text-center mb-3">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-16 sm:w-24 border-t border-[#4c0f2e] opacity-40"></div>
          <h2 className="text-lg font-semibold sm:text-3xl font-cinzelDecorative text-[#4c0f2e] whitespace-nowrap">
            Recently Viewed
          </h2>

          <div className="w-16 sm:w-24 border-t border-[#4c0f2e] opacity-40"></div>
        </div>
        <p className="text-sm text-[#5a3e36] italic mb-6">
          Continue exploring your discovered treasures
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative max-w-screen-xl mx-auto z-10">
        {/* Arrows */}

        {/* Carousel */}
        <div className="overflow-hidden" ref={carouselRef}>
          <div
            className={`flex transition-transform duration-300 ease-out ${
              recentlyViewed.length === 1 ? 'justify-center' : ''
            }`}
            style={{ transform: `translateX(-${(100 / visibleCount) * currentIndex}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {recentlyViewed.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0 px-2"
                style={{ width: `${100 / visibleCount}%` }}
              >
                <ProductItem
                  id={item.id}
                  image={[item.image]}
                  name={item.name}
                  price={item.price}
                  rating={0}
                  totalReviews={0}
                  bestseller={false}
                  inStock={true}
                  category={item.category}
                  subCategory={item.subCategory || ''}
                  sizes={[]}
                  stockQuantity={1}
                  currentPage={1}
                  categoryFilter={[]}
                  subCategoryFilter={[]}
                  sortType={'relevant'}
                  restock={item.restock}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {recentlyViewed.length > visibleCount && (
          <div className="flex justify-center mt-6 z-10 relative">
            {Array.from({ length: recentlyViewed.length - visibleCount + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className="group mx-1 focus:outline-none"
                aria-label={`Go to slide ${i + 1}`}
              >
                <span
                  className={`block h-2 w-2 rounded-full transition-all duration-300 ${
                    currentIndex === i ? 'bg-[#4c0f2e] scale-125' : 'bg-[#e4c59e]'
                  } group-hover:scale-125`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentlyViewed;
